/**
 * Base API Client (Abstract)
 *
 * Provides HTTP utility methods that concrete implementations can use.
 * This class provides the building blocks, while the interface defines the contract.
 */

import { AxiosRequestConfig } from 'axios';
import { ApiResponse, ApiError } from './IApiClient';

export abstract class BaseApiClient {
    protected requestLog: Array<{
        method: string;
        url: string;
        data?: any;
        timestamp: Date;
    }> = [];

    // ============ Protected HTTP Helpers (for subclasses to use) ============

    /**
     * Generic GET request helper
     */
    protected abstract httpGet<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;

    /**
     * Generic POST request helper
     */
    protected abstract httpPost<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;

    /**
     * Generic PUT request helper
     */
    protected abstract httpPut<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;

    /**
     * Generic PATCH request helper
     */
    protected abstract httpPatch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;

    /**
     * Generic DELETE request helper
     */
    protected abstract httpDelete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;

    // ============ Common Utilities ============

    /**
     * Create an AbortController for request cancellation
     */
    createAbortController(): AbortController {
        return new AbortController();
    }

    /**
     * Log request for debugging
     */
    protected logRequest(method: string, url: string, data?: any): void {
        this.requestLog.push({
            method,
            url,
            data,
            timestamp: new Date()
        });
    }

    /**
     * Get request log (for debugging)
     */
    getRequestLog(): Array<{ method: string; url: string; data?: any; timestamp: Date }> {
        return [...this.requestLog];
    }

    /**
     * Clear request log
     */
    clearRequestLog(): void {
        this.requestLog = [];
    }

    /**
     * Normalize URL (remove leading slash)
     */
    protected normalizeUrl(url: string): string {
        return url.startsWith('/') ? url.substring(1) : url;
    }

    /**
     * Create standard API response
     */
    protected createResponse<T>(data: T, status: number, statusText: string): ApiResponse<T> {
        return {
            data,
            status,
            statusText
        };
    }

    /**
     * Create standard API error
     */
    protected createError(message: string, status?: number, properties?: string | string[]): ApiError {
        return {
            message,
            status,
            properties
        };
    }
}
